import { X } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useDialog } from "@/lib/dialog";
import { AuthImg } from "@/components/AuthImg";
import { WaButton } from "@/components/WaButton";

const fmt = (v) => (v ? new Date(v).toLocaleString("es-EC") : null);

const FIELDS = [
  ["Nombre", (u) => u.name],
  ["Email", (u) => u.email],
  ["Teléfono", (u) => u.phone && <>{u.phone}<WaButton phone={u.phone} testid="user-info-whatsapp" /></>],
  ["Tipo de cuenta", (u) => u.account_type],
  ["Tipo de documento", (u) => u.id_type],
  ["Cédula", (u) => u.cedula],
  ["N.º de documento", (u) => u.id_number],
  ["RUC", (u) => u.ruc],
  ["Código dactilar", (u) => u.dactilar_code],
  ["Fecha de nacimiento", (u) => u.birthdate],
  ["Provincia", (u) => u.province],
  ["Ciudad", (u) => u.city],
  ["Tienda", (u) => u.store_name],
  ["Rol", (u) => u.role],
  ["Verificación", (u) => u.verification_status],
  ["Verificado el", (u) => fmt(u.verified_at)],
  ["Verificado vía", (u) => u.verified_via],
  ["Email confirmado", (u) => fmt(u.email_verified_at)],
  ["Registrado el", (u) => fmt(u.created_at)],
  ["Strikes", (u) => String(u.strikes || 0)],
  ["Baneado", (u) => u.banned && `Sí · ${fmt(u.banned_at) || ""} · ${u.banned_reason || ""}`],
  ["Suspendido", (u) => u.suspended && "Sí"],
  ["Deuda pendiente", (u) => u.debt_pending_usd && `$${u.debt_pending_usd}`],
  ["Nivel de confianza", (u) => u.trust_level],
  ["Calificaciones", (u) => u.ratings_count != null && `${u.positive_ratings_count || 0} positivas de ${u.ratings_count}`],
  ["Publicaciones aprobadas", (u) => u.pubs_approved_count != null && String(u.pubs_approved_count)],
];

const PAYOUT_LABEL = { bank_name: "Banco", account_type: "Tipo de cuenta", account_number: "N.º de cuenta", holder_name: "Titular", holder_cedula: "Cédula del titular", updated_at: "Actualizado el" };

const Grid = ({ rows, testid }) => (
  <dl className="grid grid-cols-[150px_1fr] gap-x-3 gap-y-1.5 text-sm" data-testid={testid}>
    {rows.map(([k, v]) => (
      <div key={k} className="contents">
        <dt className="text-neutral-500">{k}</dt>
        <dd className="text-white break-words">{v}</dd>
      </div>
    ))}
  </dl>
);

const Photo = ({ path, label }) => (
  <div>
    <div className="text-xs text-neutral-500 mb-1">{label}</div>
    {path ? <AuthImg path={path} width={500} className="w-full h-40 object-contain bg-[#0A0A0A] rounded border border-[#262626]" />
      : <div className="h-40 rounded border border-dashed border-[#262626] grid place-items-center text-xs text-neutral-600">Sin foto</div>}
  </div>
);

const LocalOnlyAdmin = ({ u }) => {
  const { confirm } = useDialog();
  const [busy, setBusy] = useState(false);
  const set = async (on) => {
    const desc = on ? `Sus publicaciones (activas y pausadas) solo se envían dentro de ${u.province}. Compradores de otras provincias no las verán.` : "Vuelve a enviar a todo el país.";
    if (!(await confirm({ title: on ? "¿Solo envíos en su provincia?" : "¿Quitar la restricción?", description: desc, confirmText: "Sí" }))) return;
    setBusy(true);
    try { const r = await api.post(`/admin/users/${u.id}/local-only`, { local_only: on, apply_to_active: true }); toast.success(`Listo · ${r.data.publications_updated} publicaciones actualizadas`); }
    catch (e) { toast.error(e?.response?.data?.detail || "No se pudo guardar"); } finally { setBusy(false); }
  };
  if (!u.province) return null;
  return (
    <div className="flex flex-wrap items-center gap-2 text-sm" data-testid="user-info-local-only">
      <span className="text-neutral-400">Envíos solo dentro de {u.province}:</span>
      <button disabled={busy} onClick={() => set(true)} className="px-3 py-1 rounded bg-amber-600 hover:bg-amber-700 text-white text-xs" data-testid="user-info-local-only-on">Activar</button>
      <button disabled={busy} onClick={() => set(false)} className="px-3 py-1 rounded border border-[#404040] text-neutral-300 text-xs" data-testid="user-info-local-only-off">Desactivar</button>
    </div>
  );
};

export const UserInfoModal = ({ user: u, onClose }) => {
  const rows = FIELDS.map(([k, f]) => [k, f(u)]).filter(([, v]) => v);
  const payout = Object.entries(u.payout_info || {}).filter(([, v]) => v && typeof v !== "object").map(([k, v]) => [PAYOUT_LABEL[k] || k, k === "updated_at" ? fmt(v) : String(v)]);
  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#111] border border-[#262626] rounded-md w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6 space-y-5" onClick={(e) => e.stopPropagation()} data-testid="user-info-modal">
        <div className="flex items-center justify-between">
          <h2 className="font-display text-xl">Ficha del usuario</h2>
          <button onClick={onClose} className="text-neutral-500 hover:text-white" data-testid="user-info-close"><X size={20} /></button>
        </div>
        <Grid rows={rows} testid="user-info-fields" />
        <LocalOnlyAdmin u={u} />
        {payout.length > 0 && (
          <div><h3 className="text-sm font-semibold mb-2">Datos bancarios para pagos</h3><Grid rows={payout} testid="user-info-payout" /></div>
        )}
        <div className="grid sm:grid-cols-3 gap-3">
          <Photo path={u.cedula_photo_path} label="Cédula frente" />
          <Photo path={u.cedula_back_photo_path} label="Cédula reverso" />
          <Photo path={u.selfie_photo_path} label="Selfie" />
        </div>
      </div>
    </div>
  );
};
